import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Check, X } from 'lucide-react';
import { labApi, errMsg } from './labApi';
import { rupee } from './labPrint';
import { KIND_LABEL, StatusBadge, periodText, SettlementMeta } from './settlementShared';

const PeriodCard = ({ title, s, testId }) => (
  <div className="rounded-xl border bg-white p-3 space-y-1" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{title}</p>
    <p className="text-2xl font-black text-teal-800" data-testid={`${testId}-collected`}>{rupee(s.collected)}</p>
    <p className="text-xs text-slate-600">Cash {rupee(s.cash)} • UPI {rupee(s.upi)}</p>
    <p className="text-xs text-slate-500">{s.bill_count} bills • billed {rupee(s.billed)}</p>
    {s.nursing_collected > 0 && <p className="text-xs text-sky-700">incl. nursing ECG/GMR {rupee(s.nursing_collected)}</p>}
  </div>
);

const PayForm = ({ onPay }) => {
  const [mode, setMode] = useState('upi');
  const [ref, setRef] = useState('');
  return (
    <div className="flex items-center gap-2 flex-wrap mt-2 bg-sky-50 border border-sky-200 rounded-lg p-2">
      <select value={mode} onChange={(e) => setMode(e.target.value)} className="h-8 rounded-md border px-2 text-sm" data-testid="settle-pay-mode">
        <option value="upi">UPI</option><option value="bank">Bank</option><option value="cash">Cash</option>
      </select>
      <Input placeholder="Transaction / UTR ref" value={ref} onChange={(e) => setRef(e.target.value)} className="h-8 w-48" data-testid="settle-pay-ref" />
      <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onPay(mode, ref)} data-testid="settle-pay-confirm">Mark Paid</Button>
    </div>
  );
};

const SettlementCard = ({ s, api, onChange }) => {
  const act = async (path, body, msg) => {
    try { await api.post(`/doctor/settlements/${s.id}/${path}`, body); toast.success(msg); onChange(); } catch (e) { toast.error(errMsg(e, 'Action failed')); }
  };
  const reject = () => {
    const reason = window.prompt('Reason for rejecting (optional)') ?? null;
    if (reason !== null) act('decide', { approve: false, reason }, 'Query rejected');
  };
  return (
    <div className="border rounded-xl p-3 bg-white" data-testid={`doc-settle-${s.id}`}>
      <div className="flex justify-between items-start gap-2">
        <div>
          <p className="font-bold text-sm">{KIND_LABEL[s.kind]} <span className="font-normal text-slate-500">• {periodText(s)}</span></p>
          <SettlementMeta s={s} />
        </div>
        <div className="text-right space-y-1">
          <p className="font-black text-lg">{rupee(s.amount)}</p>
          <StatusBadge status={s.status} testId={`doc-settle-status-${s.id}`} />
        </div>
      </div>
      {s.status === 'pending' && (
        <div className="flex gap-2 mt-2">
          <Button size="sm" className="h-8 bg-sky-600 hover:bg-sky-700 text-white" onClick={() => window.confirm(`Approve ${rupee(s.amount)} for ${KIND_LABEL[s.kind]}?`) && act('decide', { approve: true }, 'Approved')} data-testid={`doc-settle-approve-${s.id}`}><Check className="h-4 w-4 mr-1" /> Approve</Button>
          <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-300" onClick={reject} data-testid={`doc-settle-reject-${s.id}`}><X className="h-4 w-4 mr-1" /> Reject</Button>
        </div>
      )}
      {s.status === 'approved' && <PayForm onPay={(mode, reference) => act('pay', { mode, reference }, 'Marked as paid')} />}
    </div>
  );
};

export const LabDoctorPanel = ({ open, onOpenChange, token, onCount }) => {
  const api = useMemo(() => labApi(token), [token]);
  const [ov, setOv] = useState(null);
  const [list, setList] = useState([]);
  const load = useCallback(() => {
    api.get('/doctor/overview').then((o) => { setOv(o); onCount?.(o.pending_settlements); }).catch(() => {});
    api.get('/doctor/settlements').then(setList).catch(() => {});
  }, [api, onCount]);
  useEffect(() => { load(); }, [load, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="doc-lab-panel">
        <DialogHeader><DialogTitle>Bismi Lab &amp; ECG — Revenue &amp; Settlements</DialogTitle></DialogHeader>
        {ov && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <PeriodCard title="Today" s={ov.today} testId="doc-lab-today" />
              <PeriodCard title="This Week" s={ov.week} testId="doc-lab-week" />
              <PeriodCard title="This Month" s={ov.month} testId="doc-lab-month" />
            </div>
            <p className="text-sm text-red-600 font-semibold" data-testid="doc-lab-dues">Pending patient dues: {rupee(ov.pending_dues)} ({ov.pending_dues_count} bills)</p>
          </>
        )}
        <h4 className="font-bold mt-2">Settlement Queries</h4>
        <div className="space-y-2">
          {list.length === 0 && <p className="text-sm text-slate-400">No settlement queries</p>}
          {list.map((s) => <SettlementCard key={s.id} s={s} api={api} onChange={load} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
};
